"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { AccordionClientProps } from "@/types/data";
import { gsap } from "@/lib/gsap";

export default function AccordionClient({
  tagline,
  title,
  services,
  link,
}: AccordionClientProps) {
  const [activeItem, setActiveItem] = useState(1);

  const handleItemClick = (itemId: number) => {
    setActiveItem(activeItem === itemId ? 0 : itemId);
  };

  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const accordionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
          end: "top 50%",
          toggleActions: "play none none reverse",
        },
      });

      timeline
        .fromTo(
          headerRef.current,
          { opacity: 0, y: 30, scale: 0.95, filter: "blur(8px)" },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.8,
            ease: "power2.out",
            filter: "blur(0px)",
          }
        )
        .fromTo(
          headingRef.current,
          { opacity: 0, y: 40, filter: "blur(8px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.4"
        )
        .fromTo(
          accordionRef.current,
          { opacity: 0, y: 50, scale: 0.94 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.5"
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="section-padding bg-primary-light">
      <div className="container max-w-5xl mx-auto">
        {}
        <div className=" mb-12">
          {tagline && (
            <div ref={headerRef} className="flex items-center  mb-4 opacity-0">
              <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>

              <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
                {tagline}
              </p>
            </div>
          )}
          <div
            ref={headingRef}
            className="flex justify-between items-center lg:items-start opacity-0"
          >
            {title && (
              <h2 className="text-4xl md:text-[48px] leading-[48px] font-bold text-primary max-w-md ">
                {title}
              </h2>
            )}
            {link && (
              <Link
                href={link.url}
                className="text-accent hover:text-primary transition-colors duration-300"
              >
                <span className="sr-only">{link.text || "Link"}</span>
                <ChevronRight className="h-8 w-8" />
              </Link>
            )}
          </div>
        </div>

        {}

        <div ref={accordionRef} className="space-y-2 opacity-0">
          {services.map((service, index) => {
            const serviceId = service.id || index + 1;
            return (
              <div
                key={serviceId}
                className={` accordion-item border-b border-neutral-500/20 ${
                  activeItem === serviceId ? "active" : ""
                }`}
              >
                <div
                  className="accordion-header flex justify-between items-center py-6 cursor-pointer"
                  onClick={() => handleItemClick(serviceId)}
                >
                  <div className="flex items-baseline gap-6">
                    <span className="text-lg font-bold text-theme-secondary-light">
                      {String(serviceId).padStart(2, "0")}.
                    </span>
                    <h2 className="text-2xl font-semibold text-primary">
                      {service.title}
                    </h2>
                  </div>
                </div>
                <div className="accordion-content">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start md:pl-12">
                    <div className="md:col-span-4">
                      {service.image && (
                        <Image
                          width={321}
                          height={321}
                          src={service.image}
                          alt={service.imageAlt}
                          quality={75}
                          sizes="(max-width: 768px) 300px, (max-width: 1024px) 270px, 270px"
                          className="w-full lg:!h-[270px] h-auto rounded-lg object-cover shadow-md  max-md:size-[300px] md:mx-auto"
                          loading="lazy"
                        />
                      )}
                    </div>
                    <div className="md:col-span-4">
                      <p className="text-lg lg:text-[20px] xl:text-[22px] font-semibold mb-2 uppercase tracking-wider text-[#284D93]">
                        {service?.featuresTitle}
                      </p>
                      <ul className="space-y-1 text-sm lg:text-[18px] [&_li]:xl:text-[20px] list-disc list-outside ml-5">
                        {service.features.map((feature, featureIndex) => (
                          <li
                            className="text-[#323334]  text-sm lg:text-[18px] xl:text-[20px] font-semibold pl-2"
                            key={featureIndex}
                          >
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="md:col-span-4">
                      <p className="text-[#323334] text-sm lg:text-[18px] xl:text-[20px] font-medium mb-6">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
